import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDebouncedCallback } from 'use-debounce';
import { X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { checkSpanishLocale, cn } from 'lib/utils';
import { getQuestionnaireOptionSearchV3 } from 'services/profile/api';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { SearchChoice } from 'services/profile/type';
import { Input } from 'components/ui/input';
import Loader from 'components/Loader';
import { ScrollArea } from 'components/ui/scroll-area';
import Checkmark from './Checkmark';
import { Button } from 'components/ui/button';
import { useTranslation } from 'react-i18next';

interface MedicalConditionSearchProps {
  open: boolean;
  searchOptions: {
    eng_choices: SearchChoice[];
    spanish_choices: SearchChoice[];
  };
  onClose: () => void;
  setSelected: (conditions: SearchChoice[]) => void;
}

export default function MedicalConditionSearch({
  open,
  onClose,
  searchOptions,
  setSelected,
}: MedicalConditionSearchProps) {
  const isSpanish = checkSpanishLocale();
  const { t } = useTranslation();

  const [selectedConditions, setSelectedConditions] = useState<SearchChoice[]>(
    []
  );
  const [searchValue, setSearchValue] = useState('');
  const [conditions, setConditions] = useState<{
    eng_choices: SearchChoice[];
    spanish_choices: SearchChoice[];
  }>({
    eng_choices: [],
    spanish_choices: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setConditions(searchOptions);
  }, [searchOptions]);

  const onHandleSelection = (condition: SearchChoice) => {
    if (selectedConditions.map((c) => c.name).includes(condition.name)) {
      setSelectedConditions(
        selectedConditions.filter((c) => c.name !== condition.name)
      );
    } else {
      setSelectedConditions([...selectedConditions, condition]);
    }
  };

  const onClearAll = () => setSelectedConditions([]);

  const onSheetClose = () => {
    setSelectedConditions([]);
    onClose();
    setSearchValue('');
  };

  const debouncedSearch = useDebouncedCallback(async (value) => {
    try {
      setIsLoading(true);
      const res = await getQuestionnaireOptionSearchV3({
        code: QUESTIONNAIRE_CODE.SPECIFY_MEDICAL_CONDITION,
        search_text: value,
      });
      setConditions(
        res as unknown as {
          eng_choices: SearchChoice[];
          spanish_choices: SearchChoice[];
        }
      );
    } catch (error) {
      setConditions(searchOptions);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  const onAdd = () => {
    setSelectedConditions([
      ...selectedConditions,
      { id: null, name: searchValue },
    ]);
    setSearchValue('');
    debouncedSearch('');
  };

  const onSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const onApply = () => {
    setSelected(selectedConditions);
    onSheetClose();
  };

  const localeConditions =
    conditions?.[isSpanish ? 'spanish_choices' : 'eng_choices'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={cn('max-w-lg sm:px-4')}
        // onOpenAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className='text-center'>
            {t('browse_conditions')}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {t('search_conditions')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <Input
              onChange={(e) => onSearch(e.target.value)}
              value={searchValue}
              placeholder={`${t('search_conditions')}...`}
            />
            <div className='mt-4'>
              {isLoading ? (
                <Loader className='h-[30vh]' loaderSize='size-6' />
              ) : localeConditions?.length ? (
                <ScrollArea className='h-[30vh]'>
                  <div className='space-y-2'>
                    {localeConditions.map((condition) => {
                      const isSelected = selectedConditions.includes(condition);
                      return (
                        <motion.div
                          key={condition.id}
                          onClick={() => onHandleSelection(condition)}
                          className={cn(
                            'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-gray-600 gap-4',
                            isSelected &&
                              'border-primary text-primary bg-[#E1EBFF]'
                          )}
                          whileTap={{ scale: 0.98 }}
                        >
                          <p className='text-sm leading-5'>{condition.name}</p>

                          <Checkmark isSelected={isSelected} />
                        </motion.div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className='h-[30vh] flex-col items-center justify-center text-sm text-[#656D76] flex space-y-2'>
                  <div className='flex items-center justify-center rounded-md bg-rose-100 size-10'>
                    <X className='size-6 text-rose-500' />
                  </div>
                  <p>{t('condition_not_found')}</p>
                  <Button onClick={onAdd} size={'sm'}>
                    {t('add_anyway')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className='flex items-center justify-between'>
              <h1 className='font-semibold'>{t('selected')}</h1>
              <Button
                variant='link'
                className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
                disabled={!selectedConditions.length}
                onClick={onClearAll}
              >
                {t('clear_all')}
              </Button>
            </div>
            <ScrollArea className='h-[20vh]'>
              <div className='flex flex-wrap gap-2'>
                {selectedConditions?.length ? (
                  selectedConditions.map((condition) => {
                    return (
                      <motion.div
                        key={condition.name}
                        className={cn(
                          'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl h-fit min-h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                        )}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.15 }}
                        onClick={() => onHandleSelection(condition)}
                      >
                        <p className='leading-5'>{condition.name}</p>
                        <X className='w-4 h-4' />
                      </motion.div>
                    );
                  })
                ) : (
                  <div>
                    <div
                      className={cn(
                        'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
                      )}
                    >
                      <p className='leading-5'>{t('none')}</p>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <Button onClick={onApply} className='w-full'>
            {t('apply')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
